'use client'

import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ExternalLink, RefreshCw, AlertCircle, Music2, Calendar, TrendingDown, Activity, Zap, Copy, Check, Filter } from 'lucide-react'
import { toast } from 'sonner'
import type { AnalysisResult, SongStatRow, BustoutRow } from '@/lib/analysis'

interface SongsCatalogSummary {
  loaded: boolean
  total: number
  originals: number
  covers: number
}

interface ApiResponse {
  ok: boolean
  result: AnalysisResult | null
  lastRefresh?: {
    createdAt: string
    source: string
    preShows: number
    postShows: number
    totalEntries: number
    missingCount: number
    regularCount: number
  } | null
  boundaryDate: string
  prePeriodStart: string
  postPeriodStart: string
  songsCatalog?: SongsCatalogSummary
  excludeCovers?: boolean
  message?: string
}

// Years to expose in the refresh dialog: 2023 (pre-period start) through the current calendar year.
// When 2027 arrives, 2027 is automatically added to the list.
const REFRESH_YEARS = (() => {
  const startYear = 2023 // PRE_PERIOD_START = '2023-02-16'
  const currentYear = new Date().getFullYear()
  const years: string[] = []
  for (let y = startYear; y <= currentYear; y++) years.push(String(y))
  return years
})()
const CURRENT_YEAR = String(new Date().getFullYear())
const API_URL_FOR_YEAR = (year: string) =>
  `https://allthings.umphreys.com/api/v2/setlists/showyear/${year}.json?order_by=showdate`

export default function Home() {
  const [data, setData] = useState<ApiResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshOpen, setRefreshOpen] = useState(false)
  const [sortKey, setSortKey] = useState<'prePeriodCount' | 'lastPreDate' | 'songName'>('prePeriodCount')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [bustoutSortKey, setBustoutSortKey] = useState<'gapDays' | 'showsBetween' | 'firstPostDate' | 'songName'>('gapDays')
  const [bustoutSortDir, setBustoutSortDir] = useState<'asc' | 'desc'>('desc')
  const [searchQuery, setSearchQuery] = useState('')
  const [copiedCard, setCopiedCard] = useState<'missing' | 'still' | 'bustouts' | null>(null)
  // excludeCovers is initialized to null (unknown); after first load we sync it
  // with whatever the server returns (which defaults to true if a catalog is loaded).
  // Once the user explicitly toggles, we use their value.
  const [excludeCovers, setExcludeCovers] = useState<boolean | null>(null)
  const [excludeCoversUserSet, setExcludeCoversUserSet] = useState(false)

  async function loadData(ec: boolean | null = null) {
    setLoading(true)
    try {
      const effectiveEc = ec ?? excludeCovers ?? true
      const r = await fetch(`/api/data?excludeCovers=${effectiveEc}`, { cache: 'no-store' })
      const j = (await r.json()) as ApiResponse
      setData(j)
      // Sync local state with server's response (unless the user has explicitly toggled)
      if (!excludeCoversUserSet) {
        setExcludeCovers(j.excludeCovers ?? true)
      }
    } catch (e) {
      toast.error(`Failed to load analysis: ${(e as Error).message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  async function toggleExcludeCovers(next: boolean) {
    setExcludeCoversUserSet(true)
    setExcludeCovers(next)
    await loadData(next)
  }

  const result = data?.result
  const sortedMissing = useMemo(() => {
    if (!result) return []
    let list = result.missingSongs
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      list = list.filter((s) => s.songName.toLowerCase().includes(q))
    }
    const sorted = [...list].sort((a, b) => {
      let cmp = 0
      if (sortKey === 'songName') cmp = a.songName.localeCompare(b.songName)
      else if (sortKey === 'lastPreDate') cmp = (a.lastPreDate ?? '').localeCompare(b.lastPreDate ?? '')
      else cmp = a.prePeriodCount - b.prePeriodCount
      return sortDir === 'desc' ? -cmp : cmp
    })
    return sorted
  }, [result, sortKey, sortDir, searchQuery])

  const sortedStillPlayed = useMemo(() => {
    if (!result) return []
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      return result.stillPlayedSongs.filter((s) => s.songName.toLowerCase().includes(q))
    }
    return result.stillPlayedSongs
  }, [result, searchQuery])

  const sortedBustouts = useMemo(() => {
    if (!result) return []
    let list = result.bustoutSongs
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      list = list.filter((s) => s.songName.toLowerCase().includes(q))
    }
    const sorted = [...list].sort((a, b) => {
      let cmp = 0
      if (bustoutSortKey === 'songName') cmp = a.songName.localeCompare(b.songName)
      else if (bustoutSortKey === 'firstPostDate') cmp = a.firstPostDate.localeCompare(b.firstPostDate)
      else if (bustoutSortKey === 'showsBetween') {
        // Treat null (revivals) as -1 so they sink to the bottom when sorting desc
        const av = a.showsBetween ?? -1
        const bv = b.showsBetween ?? -1
        cmp = av - bv
      } else {
        // gapDays — same null treatment
        const av = a.gapDays ?? -1
        const bv = b.gapDays ?? -1
        cmp = av - bv
      }
      return bustoutSortDir === 'desc' ? -cmp : cmp
    })
    return sorted
  }, [result, bustoutSortKey, bustoutSortDir, searchQuery])

  function toggleSort(key: 'prePeriodCount' | 'lastPreDate' | 'songName') {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'songName' ? 'asc' : 'desc')
    }
  }

  function toggleBustoutSort(key: 'gapDays' | 'showsBetween' | 'firstPostDate' | 'songName') {
    if (bustoutSortKey === key) {
      setBustoutSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setBustoutSortKey(key)
      setBustoutSortDir(key === 'songName' ? 'asc' : 'desc')
    }
  }

  function fmtDate(d: string | null): string {
    if (!d) return ''
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Build a TSV (tab-separated) string from song stat rows. Pasted directly into Excel/Sheets.
  function songsToTsv(rows: SongStatRow[], variant: 'missing' | 'still'): string {
    const headers =
      variant === 'still'
        ? ['#', 'Song', 'Pre-plays', 'Last played (pre)', 'Post-plays', 'Last played (post)']
        : ['#', 'Song', 'Pre-plays', 'Last played (pre)']
    const lines = [headers.join('\t')]
    rows.forEach((s, i) => {
      const base = [String(i + 1), s.songName, String(s.prePeriodCount), fmtDate(s.lastPreDate)]
      if (variant === 'still') {
        base.push(String(s.postPeriodCount), fmtDate(s.lastPostDate))
      }
      lines.push(base.join('\t'))
    })
    return lines.join('\n')
  }

  function bustoutsToTsv(rows: BustoutRow[]): string {
    const headers = ['#', 'Song', 'Pre-plays', 'Last played (pre)', 'Bustout date', 'Gap (days)', 'Shows between', 'Revival']
    const lines = [headers.join('\t')]
    rows.forEach((s, i) => {
      lines.push([
        String(i + 1),
        s.songName,
        s.prePeriodCount === 0 ? '' : String(s.prePeriodCount),
        fmtDate(s.lastPreDate),
        fmtDate(s.firstPostDate),
        s.gapDays === null ? '' : String(s.gapDays),
        s.showsBetween === null ? '' : String(s.showsBetween),
        s.isRevival ? 'Yes' : 'No',
      ].join('\t'))
    })
    return lines.join('\n')
  }

  async function copyToClipboard(text: string, card: 'missing' | 'still' | 'bustouts', label: string) {
    const fallbackCopy = () => {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      if (!ok) throw new Error('execCommand returned false')
    }

    try {
      // Prefer the async clipboard API; fall back to execCommand for older browsers
      // or when permission is denied (headless browsers, insecure contexts, etc.).
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text)
        } catch {
          fallbackCopy()
        }
      } else {
        fallbackCopy()
      }
      setCopiedCard(card)
      setTimeout(() => setCopiedCard((c) => (c === card ? null : c)), 1800)
      toast.success(`Copied ${label} to clipboard`)
    } catch (e) {
      toast.error(`Copy failed: ${(e as Error).message}`)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-zinc-50 to-zinc-100 dark:from-zinc-950 dark:to-zinc-900">
      <Header
        data={data}
        onRefreshClick={() => setRefreshOpen(true)}
        loading={loading}
      />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {loading ? (
          <LoadingState />
        ) : !result ? (
          <EmptyState onRefreshClick={() => setRefreshOpen(true)} message={data?.message} />
        ) : (
          <>
            <StatsGrid
              result={result}
              lastRefresh={data?.lastRefresh}
              songsCatalog={data?.songsCatalog}
              excludeCovers={excludeCovers ?? true}
              onToggleExcludeCovers={toggleExcludeCovers}
            />

            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-rose-600" />
                    Missing Songs — Not Played Since {data?.boundaryDate}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {result.missingSongs.length} song{result.missingSongs.length === 1 ? '' : 's'} played ≥{result.threshold}× between{' '}
                    {result.prePeriodDateRange?.from} and {result.prePeriodDateRange?.to}, but not once since{' '}
                    {data?.postPeriodStart}.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Input
                    placeholder="Filter songs…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 sm:w-60"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(songsToTsv(sortedMissing, 'missing'), 'missing', `${sortedMissing.length} missing song${sortedMissing.length === 1 ? '' : 's'}`)}
                    disabled={sortedMissing.length === 0}
                    title="Copy table to clipboard (TSV — pastes into Excel/Sheets)"
                  >
                    {copiedCard === 'missing' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {sortedMissing.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-12 text-center">
                    {searchQuery ? 'No matching songs.' : 'No missing songs to display.'}
                  </div>
                ) : (
                  <SongsTable
                    rows={sortedMissing}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={toggleSort}
                    variant="missing"
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-emerald-600" />
                    Songs Still in Rotation
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {result.stillPlayedSongs.length} regular songs (≥{result.threshold}× pre-boundary) that have been
                    played at least once since {data?.postPeriodStart}.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(songsToTsv(sortedStillPlayed, 'still'), 'still', `${sortedStillPlayed.length} song${sortedStillPlayed.length === 1 ? '' : 's'} still in rotation`)}
                  disabled={sortedStillPlayed.length === 0}
                  title="Copy table to clipboard (TSV — pastes into Excel/Sheets)"
                  className="self-start sm:self-auto shrink-0"
                >
                  {copiedCard === 'still' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </CardHeader>
              <CardContent>
                {sortedStillPlayed.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-12 text-center">
                    {searchQuery ? 'No matching songs.' : 'No songs in this list yet.'}
                  </div>
                ) : (
                  <SongsTable rows={sortedStillPlayed} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} variant="still" />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    Bustouts Since {data?.boundaryDate}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {result.bustoutSongs.length} song{result.bustoutSongs.length === 1 ? '' : 's'} played since{' '}
                    {data?.postPeriodStart} after a long absence. <span className="font-medium">Gap (days)</span> is the
                    time between the song&apos;s last pre-boundary play and its first post-boundary play.{' '}
                    <span className="font-medium">Shows between</span> counts how many distinct shows the band played
                    between those two performances — the standard allthings.umphreys.com bustout measure. Songs with{' '}
                    <Badge variant="outline" className="ml-1 align-middle">Revival</Badge> had no prior play in the
                    3-year lookback window (true debut or pre-2023 last play).
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(bustoutsToTsv(sortedBustouts), 'bustouts', `${sortedBustouts.length} bustout${sortedBustouts.length === 1 ? '' : 's'}`)}
                  disabled={sortedBustouts.length === 0}
                  title="Copy table to clipboard (TSV — pastes into Excel/Sheets)"
                  className="self-start sm:self-auto shrink-0"
                >
                  {copiedCard === 'bustouts' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </CardHeader>
              <CardContent>
                {sortedBustouts.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-12 text-center">
                    {searchQuery ? 'No matching songs.' : 'No bustouts to display yet.'}
                  </div>
                ) : (
                  <BustoutsTable
                    rows={sortedBustouts}
                    sortKey={bustoutSortKey}
                    sortDir={bustoutSortDir}
                    onSort={toggleBustoutSort}
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <Footer />

      <RefreshDialog
        open={refreshOpen}
        onOpenChange={setRefreshOpen}
        onRefreshed={async (summary) => {
          setRefreshOpen(false)
          // After refresh, drop the user-set excludeCovers flag so the server default
          // (which now accounts for the freshly-loaded songs catalog) takes effect.
          setExcludeCoversUserSet(false)
          await loadData()
          const bits: string[] = []
          if (summary.updatedYears.length > 0) {
            bits.push(
              `Updated ${summary.updatedYears.join(', ')} — ${summary.insertedEntries.toLocaleString()} entr${
                summary.insertedEntries === 1 ? 'y' : 'ies'
              }`
            )
          }
          if (summary.songsCatalogUpdated) {
            bits.push('Songs catalog loaded (covers will be filtered)')
          }
          if (bits.length === 0) bits.push('Analysis refreshed')
          toast.success(bits.join(' · '))
        }}
      />
    </div>
  )
}

function Header({ data, onRefreshClick, loading }: { data: ApiResponse | null; onRefreshClick: () => void; loading: boolean }) {
  return (
    <header className="border-b bg-white dark:bg-zinc-950 sticky top-0 z-30 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-amber-500 to-rose-600 flex items-center justify-center shadow-sm">
            <Music2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Umphrey&apos;s McGee — Setlist Discontinuation Tracker</h1>
            <p className="text-xs text-muted-foreground">
              Comparing songs played before vs. after {data?.boundaryDate ?? '2/15/2026'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data?.lastRefresh && (
            <div className="hidden sm:block text-right">
              <div className="text-xs text-muted-foreground">Last refreshed</div>
              <div className="text-sm font-medium">
                {new Date(data.lastRefresh.createdAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </div>
            </div>
          )}
          <Button onClick={onRefreshClick} variant="default" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>
      </div>
    </header>
  )
}

function StatsGrid({
  result,
  lastRefresh,
  songsCatalog,
  excludeCovers,
  onToggleExcludeCovers,
}: {
  result: AnalysisResult
  lastRefresh?: ApiResponse['lastRefresh']
  songsCatalog?: SongsCatalogSummary
  excludeCovers: boolean
  onToggleExcludeCovers: (next: boolean) => void
}) {
  const stats = [
    {
      label: 'Pre-period shows',
      value: result.preShowsCount,
      sub: result.prePeriodDateRange
        ? `${result.prePeriodDateRange.from} → ${result.prePeriodDateRange.to}`
        : '—',
      icon: Calendar,
      tint: 'text-sky-600',
    },
    {
      label: 'Post-period shows',
      value: result.postShowsCount,
      sub: result.postPeriodDateRange
        ? `${result.postPeriodDateRange.from} → ${result.postPeriodDateRange.to}`
        : '—',
      icon: Calendar,
      tint: 'text-indigo-600',
    },
    {
      label: `Regular songs (≥${result.threshold}×)`,
      value: result.regularSongs.length,
      sub: `out of ${result.totalSongsPre} distinct pre-period songs`,
      icon: Music2,
      tint: 'text-amber-600',
    },
    {
      label: 'Missing songs',
      value: result.missingSongs.length,
      sub: 'regular before, zero since',
      icon: TrendingDown,
      tint: 'text-rose-600',
    },
  ]

  return (
    <div className="space-y-4">
      {/* Cover-filter toggle row — appears above the stat cards when a songs catalog is loaded */}
      {songsCatalog?.loaded && (
        <Card className="bg-amber-50/50 dark:bg-amber-950/10 border-amber-200 dark:border-amber-900">
          <CardContent className="py-3 px-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium">
                Cover songs {excludeCovers ? 'excluded' : 'included'}
              </span>
              <Badge variant="secondary" className="text-xs">
                {songsCatalog.total.toLocaleString()} songs in catalog ·{' '}
                {songsCatalog.originals.toLocaleString()} UM originals ·{' '}
                {songsCatalog.covers.toLocaleString()} covers
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {result.coversExcludedCount != null && excludeCovers && (
                <span className="text-xs text-muted-foreground">
                  {result.coversExcludedCount.toLocaleString()} cover entr
                  {result.coversExcludedCount === 1 ? 'y' : 'ies'} filtered from analysis
                </span>
              )}
              <ToggleExcludeCovers value={excludeCovers} onChange={onToggleExcludeCovers} />
            </div>
          </CardContent>
        </Card>
      )}
      {!songsCatalog?.loaded && (
        <Card className="bg-muted/30 border-dashed">
          <CardContent className="py-3 px-4 flex items-center gap-2 text-xs text-muted-foreground">
            <Filter className="h-4 w-4" />
            <span>
              Cover songs are <span className="font-medium">included</span> in this analysis.
              Load the songs catalog (in Refresh Data) to enable cover filtering.
            </span>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2 text-xs">
                <s.icon className={`h-3.5 w-3.5 ${s.tint}`} />
                {s.label}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tracking-tight">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

function ToggleExcludeCovers({
  value,
  onChange,
}: {
  value: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-center gap-1 rounded-lg border bg-card p-0.5">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
          value
            ? 'bg-amber-600 text-white shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        aria-pressed={value}
      >
        Exclude covers
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
          !value
            ? 'bg-amber-600 text-white shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        aria-pressed={!value}
      >
        Include all
      </button>
    </div>
  )
}

function SongsTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  variant,
}: {
  rows: SongStatRow[]
  sortKey: 'prePeriodCount' | 'lastPreDate' | 'songName'
  sortDir: 'asc' | 'desc'
  onSort: (k: 'prePeriodCount' | 'lastPreDate' | 'songName') => void
  variant: 'missing' | 'still'
}) {
  return (
    <div className="rounded-md border overflow-hidden">
      <div className="max-h-[28rem] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>
                <button
                  onClick={() => onSort('songName')}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Song
                  {sortKey === 'songName' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button
                  onClick={() => onSort('prePeriodCount')}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Pre-plays
                  {sortKey === 'prePeriodCount' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </TableHead>
              <TableHead>
                <button
                  onClick={() => onSort('lastPreDate')}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Last played (pre)
                  {sortKey === 'lastPreDate' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </TableHead>
              {variant === 'still' && <TableHead className="text-right">Post-plays</TableHead>}
              {variant === 'still' && <TableHead>Last played (post)</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s, i) => (
              <TableRow key={`${s.songName}-${i}`}>
                <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                <TableCell className="font-medium">{s.songName}</TableCell>
                <TableCell className="text-right tabular-nums">{s.prePeriodCount}</TableCell>
                <TableCell>
                  {s.lastPreDate
                    ? new Date(s.lastPreDate + 'T00:00:00').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : '—'}
                </TableCell>
                {variant === 'still' && (
                  <TableCell className="text-right tabular-nums">
                    <Badge variant="secondary">{s.postPeriodCount}</Badge>
                  </TableCell>
                )}
                {variant === 'still' && (
                  <TableCell>
                    {s.lastPostDate
                      ? new Date(s.lastPostDate + 'T00:00:00').toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function BustoutsTable({
  rows,
  sortKey,
  sortDir,
  onSort,
}: {
  rows: BustoutRow[]
  sortKey: 'gapDays' | 'showsBetween' | 'firstPostDate' | 'songName'
  sortDir: 'asc' | 'desc'
  onSort: (k: 'gapDays' | 'showsBetween' | 'firstPostDate' | 'songName') => void
}) {
  return (
    <div className="rounded-md border overflow-hidden">
      <div className="max-h-[32rem] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>
                <button
                  onClick={() => onSort('songName')}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Song
                  {sortKey === 'songName' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </TableHead>
              <TableHead className="text-right">Pre-plays</TableHead>
              <TableHead>Last played (pre)</TableHead>
              <TableHead>
                <button
                  onClick={() => onSort('firstPostDate')}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Bustout date
                  {sortKey === 'firstPostDate' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button
                  onClick={() => onSort('gapDays')}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Gap (days)
                  {sortKey === 'gapDays' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button
                  onClick={() => onSort('showsBetween')}
                  className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                >
                  Shows between
                  {sortKey === 'showsBetween' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s, i) => (
              <TableRow key={`${s.songName}-${i}`}>
                <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.songName}
                    {s.isRevival && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                        Revival
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {s.prePeriodCount === 0 ? '—' : s.prePeriodCount}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {s.lastPreDate
                    ? new Date(s.lastPreDate + 'T00:00:00').toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : '—'}
                </TableCell>
                <TableCell>
                  {new Date(s.firstPostDate + 'T00:00:00').toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {s.gapDays === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <Badge variant={s.gapDays >= 365 ? 'default' : 'secondary'} className="tabular-nums">
                      {s.gapDays.toLocaleString()}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {s.showsBetween === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className={s.showsBetween >= 50 ? 'font-semibold text-amber-600' : ''}>
                      {s.showsBetween}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-3 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-9 w-16" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-80" />
          <Skeleton className="h-3 w-96 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}

function EmptyState({ onRefreshClick, message }: { onRefreshClick: () => void; message?: string }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-16 flex flex-col items-center text-center gap-4">
        <div className="h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
          <Music2 className="h-7 w-7 text-amber-600" />
        </div>
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">No setlist data loaded yet</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {message ??
              'This dashboard analyzes songs played before vs. after 2/15/2026. Load setlist data from allthings.umphreys.com to see the analysis.'}
          </p>
        </div>
        <Button onClick={onRefreshClick} size="lg">
          <RefreshCw className="h-4 w-4 mr-2" />
          Load Setlist Data
        </Button>
      </CardContent>
    </Card>
  )
}

function Footer() {
  return (
    <footer className="mt-auto border-t bg-white dark:bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
        <p>
          Data source: <a className="underline hover:text-foreground" href="https://allthings.umphreys.com/api/docs/" target="_blank" rel="noreferrer">allthings.umphreys.com API</a> · UMbase v465 by Adam Scheinberg
        </p>
        <p>Pre-period: 2/16/2023 → 2/15/2026 · Post-period: 2/16/2026 → today</p>
      </div>
    </footer>
  )
}

// ---------------------------------------------------------------------------
// Refresh dialog — multi-step paste workflow
// ---------------------------------------------------------------------------

interface LoadedYearInfo {
  year: string
  entryCount: number
  showCount: number
  lastRefreshedAt: string | null
}

type YearStatus =
  | { kind: 'checking' } // status not yet known — /api/loaded-years fetch in flight
  | { kind: 'required'; reason: string } // confirmed not loaded — user must paste to enable analysis
  | { kind: 'current'; loaded: LoadedYearInfo | null } // current calendar year — likely to grow
  | { kind: 'loaded'; loaded: LoadedYearInfo } // past year already in DB — refresh optional

function computeYearStatus(
  year: string,
  loaded: LoadedYearInfo | undefined,
  isChecking: boolean
): YearStatus {
  if (year === CURRENT_YEAR) {
    return { kind: 'current', loaded: loaded ?? null }
  }
  if (isChecking) {
    // Past year with unknown status — /api/loaded-years hasn't returned yet.
    // Don't render as Required (which would expand the textarea and confuse the user);
    // render as Checking instead (collapsed, neutral).
    return { kind: 'checking' }
  }
  if (!loaded) {
    return { kind: 'required', reason: 'Not loaded yet — required for analysis' }
  }
  return { kind: 'loaded', loaded }
}

function RefreshDialog({
  open,
  onOpenChange,
  onRefreshed,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onRefreshed: (summary: {
    updatedYears: string[]
    insertedEntries: number
    songsCatalogUpdated: boolean
  }) => Promise<void> | void
}) {
  // yearInputs is keyed by year; only years the user actually pasted JSON for are present
  const [yearInputs, setYearInputs] = useState<Record<string, string>>({})
  const [songsInput, setSongsInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorDetail, setErrorDetail] = useState<{
    preview?: string
    hint?: string
    receivedLength?: number
    expectedUrl?: string
  } | null>(null)
  const [loadedYears, setLoadedYears] = useState<LoadedYearInfo[] | null>(null)
  const [songsCatalog, setSongsCatalog] = useState<SongsCatalogSummary | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(false)

  // Fetch loaded-years status (which now also includes songsCatalog) whenever the dialog opens
  useEffect(() => {
    if (!open) {
      setError(null)
      setErrorDetail(null)
      return
    }
    setLoadingStatus(true)
    fetch('/api/loaded-years', { cache: 'no-store' })
      .then((r) => r.json())
      .then(
        (j: {
          ok?: boolean
          years?: LoadedYearInfo[]
          songsCatalog?: SongsCatalogSummary
        }) => {
          if (j.ok && Array.isArray(j.years)) {
            setLoadedYears(j.years)
          } else {
            setLoadedYears([])
          }
          setSongsCatalog(j.songsCatalog ?? { loaded: false, total: 0, originals: 0, covers: 0 })
        }
      )
      .catch(() => {
        setLoadedYears([])
        setSongsCatalog({ loaded: false, total: 0, originals: 0, covers: 0 })
      })
      .finally(() => setLoadingStatus(false))
  }, [open])

  // Build a quick lookup: year → LoadedYearInfo | undefined
  const loadedByYear = useMemo(() => {
    const m = new Map<string, LoadedYearInfo>()
    if (loadedYears) for (const y of loadedYears) m.set(y.year, y)
    return m
  }, [loadedYears])

  // For each year in REFRESH_YEARS, compute its status.
  // While loaded-years is still being fetched (null), past years render as 'checking'
  // instead of 'required' so users don't see them temporarily appear as expanded textareas.
  const isChecking = loadedYears === null
  const yearStatuses = useMemo(() => {
    return REFRESH_YEARS.map((year) => ({
      year,
      status: computeYearStatus(year, loadedByYear.get(year), isChecking),
    }))
  }, [loadedByYear, isChecking])

  // How many years still need to be loaded (required)?
  // Years in 'checking' state don't count — we don't yet know if they're required.
  const requiredCount = yearStatuses.filter((y) => y.status.kind === 'required').length
  // How many years has the user pasted JSON for in this session?
  const filledYears = REFRESH_YEARS.filter((y) => (yearInputs[y] ?? '').trim().length > 0)
  // Did the user paste a songs catalog?
  const hasSongsInput = songsInput.trim().length > 0
  // Is anything ready to submit?
  const canSubmit = filledYears.length > 0 || hasSongsInput

  async function handleSubmit() {
    setError(null)
    setErrorDetail(null)
    if (!canSubmit) {
      setError('Paste JSON for at least one year or the songs catalog before submitting.')
      return
    }
    setSubmitting(true)
    try {
      let songsCatalogUpdated = false

      // Step 1: If songs catalog was pasted, submit it first (separate endpoint)
      if (hasSongsInput) {
        const songsResp = await fetch('/api/songs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ songs: songsInput, source: 'user-paste' }),
        })
        const songsJson = (await songsResp.json()) as {
          ok?: boolean
          error?: string
          preview?: string
          hint?: string
          receivedLength?: number
          expectedUrl?: string
          total?: number
          originals?: number
          covers?: number
        }
        if (!songsResp.ok || !songsJson.ok) {
          // Preserve structured error info (preview/hint) so the UI can show them
          setError(`Songs catalog: ${songsJson.error ?? `Server returned ${songsResp.status}`}`)
          if (songsJson.preview || songsJson.hint || songsJson.expectedUrl) {
            setErrorDetail({
              preview: songsJson.preview,
              hint: songsJson.hint,
              receivedLength: songsJson.receivedLength,
              expectedUrl: songsJson.expectedUrl,
            })
          }
          return
        }
        songsCatalogUpdated = true
      }

      // Step 2: If any years were pasted, submit them via /api/refresh (incremental)
      let updatedYears: string[] = []
      let insertedEntries = 0
      if (filledYears.length > 0) {
        const payload: Record<string, string> = {}
        for (const y of filledYears) payload[y] = yearInputs[y]
        const r = await fetch('/api/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ years: payload, source: 'user-paste' }),
        })
        const j = (await r.json()) as {
          ok?: boolean
          error?: string
          perYearCounts?: Record<string, number>
          parseErrors?: Record<string, string>
          updatedYears?: string[]
          insertedEntries?: number
        }
        if (!r.ok || !j.ok) {
          setError(j.error ?? `Server returned ${r.status}`)
          return
        }
        updatedYears = j.updatedYears ?? filledYears
        insertedEntries = j.insertedEntries ?? 0
      }

      // Clear inputs on success
      setYearInputs({})
      setSongsInput('')
      await onRefreshed({
        updatedYears,
        insertedEntries,
        songsCatalogUpdated,
      })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  // Submit button label depends on what's being submitted
  const submitLabel = (() => {
    if (submitting) return null // spinner handles it
    if (!canSubmit) {
      return requiredCount > 0 ? 'Paste required year(s) to enable' : 'Paste a year or songs catalog to refresh'
    }
    const parts: string[] = []
    if (filledYears.length === 1) parts.push(`update ${filledYears[0]}`)
    else if (filledYears.length > 1) parts.push(`update ${filledYears.length} years`)
    if (hasSongsInput) parts.push(parts.length > 0 ? 'and songs catalog' : 'load songs catalog')
    return `Analyze & Save — ${parts.join(' ')}`
  })()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-w-[calc(100%-1.5rem)] max-h-[92vh] overflow-y-auto p-5 sm:p-7">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Refresh Setlist Data
          </DialogTitle>
          <DialogDescription>
            The allthings.umphreys.com API is behind Cloudflare bot protection, so the dashboard can&apos;t fetch
            it directly from the server. Open each year&apos;s API URL in your browser, copy the JSON response,
            and paste it below. The dashboard will parse and analyze everything locally.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Incremental refresh — only paste what&apos;s changed <span className="text-xs font-normal opacity-60 ml-1">(dialog v4 — cover filtering)</span></AlertTitle>
          <AlertDescription>
            Past years (once loaded) don&apos;t change — only the current year ({CURRENT_YEAR}) grows as the band
            plays new shows. After your first load, you typically only need to re-paste {CURRENT_YEAR} to pick up
            new shows. Other years stay loaded in the database and continue contributing to the analysis.
            Load the <span className="font-medium">songs catalog</span> once (top row) to enable filtering out cover songs.
          </AlertDescription>
        </Alert>

        {loadingStatus && (
          <div className="text-sm text-muted-foreground flex items-center gap-2 py-2">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Checking which years are already loaded…
          </div>
        )}

        <div className="space-y-4">
          {/* Songs catalog row — loaded once, used to filter covers */}
          <SongsCatalogRow
            value={songsInput}
            onChange={setSongsInput}
            catalog={songsCatalog}
          />
          {/* Year rows */}
          {yearStatuses.map(({ year, status }) => (
            <YearInputRow
              key={`${year}-${status.kind}`}
              year={year}
              status={status}
              value={yearInputs[year] ?? ''}
              onChange={(v) => setYearInputs((p) => ({ ...p, [year]: v }))}
            />
          ))}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Refresh failed</AlertTitle>
            <AlertDescription className="space-y-2">
              <div className="font-mono text-xs">{error}</div>
              {errorDetail?.preview && (
                <div className="mt-2 pt-2 border-t border-destructive/30">
                  <div className="text-xs font-medium mb-1">Preview of what was pasted:</div>
                  <pre className="text-[11px] font-mono bg-muted/50 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all max-h-24 overflow-y-auto">
{errorDetail.preview}
                  </pre>
                  {errorDetail.receivedLength != null && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                      ({errorDetail.receivedLength.toLocaleString()} chars total)
                    </div>
                  )}
                </div>
              )}
              {errorDetail?.hint && (
                <div className="text-xs mt-2 leading-relaxed">
                  <span className="font-medium">How to fix:</span> {errorDetail.hint}
                </div>
              )}
              {errorDetail?.expectedUrl && (
                <div className="text-xs mt-1">
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-destructive underline"
                    onClick={() => window.open(errorDetail.expectedUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Open the correct API URL in a new tab →
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !canSubmit}>
            {submitting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Analyzing…
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const SONGS_API_URL = 'https://allthings.umphreys.com/api/v2/songs.json'

function SongsCatalogRow({
  value,
  onChange,
  catalog,
}: {
  value: string
  onChange: (v: string) => void
  catalog: SongsCatalogSummary | null
}) {
  const [showField, setShowField] = useState(false)

  const trimmed = value.trim()
  const looksValid = trimmed.length > 0 && (trimmed.startsWith('{') || trimmed.startsWith('['))
  let parsedHint = ''
  if (looksValid) {
    try {
      const parsed = JSON.parse(trimmed)
      let arr: unknown[] = []
      if (Array.isArray(parsed)) arr = parsed
      else if (parsed && typeof parsed === 'object') {
        const p = parsed as Record<string, unknown>
        if (Array.isArray(p.data)) arr = p.data
        else if (p.data && typeof p.data === 'object') {
          const d = p.data as Record<string, unknown>
          for (const k of Object.keys(d)) {
            if (Array.isArray(d[k])) {
              arr = d[k] as unknown[]
              break
            }
          }
        }
      }
      parsedHint = `${arr.length.toLocaleString()} songs parsed`
    } catch {
      parsedHint = 'invalid JSON'
    }
  }

  const isLoaded = catalog?.loaded
  const loadedSummary = isLoaded
    ? `${catalog!.total.toLocaleString()} songs · ${catalog!.originals.toLocaleString()} UM originals · ${catalog!.covers.toLocaleString()} covers`
    : null

  // Status badge for the catalog row
  const statusBadge = (() => {
    if (looksValid) {
      return (
        <Badge variant={parsedHint === 'invalid JSON' ? 'destructive' : 'secondary'}>
          {parsedHint}
        </Badge>
      )
    }
    if (isLoaded) {
      return (
        <Badge
          variant="secondary"
          className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300"
        >
          <Check className="h-3 w-3" />
          Catalog loaded
        </Badge>
      )
    }
    if (catalog === null) {
      // Catalog status not yet fetched — show neutral checking state
      return (
        <Badge variant="secondary" className="gap-1">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Checking…
        </Badge>
      )
    }
    return (
      <Badge variant="secondary" className="gap-1 bg-sky-100 text-sky-700 hover:bg-sky-100 dark:bg-sky-950 dark:text-sky-300">
        <Filter className="h-3 w-3" />
        Recommended — enables cover filtering
      </Badge>
    )
  })()

  return (
    <div
      className={`rounded-lg border p-4 space-y-3 ${
        !isLoaded && catalog !== null
          ? 'border-sky-300 bg-sky-50/40 dark:bg-sky-950/10'
          : 'border-amber-200 bg-amber-50/30 dark:bg-amber-950/10 dark:border-amber-900'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <Label className="flex items-center gap-2 font-medium text-sm flex-wrap">
          <Filter className="h-4 w-4 text-muted-foreground" />
          Songs catalog
          {statusBadge}
        </Label>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(SONGS_API_URL, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Open API URL
          </Button>
          {isLoaded && !looksValid ? (
            <Button variant="ghost" size="sm" onClick={() => setShowField((s) => !s)}>
              {showField ? 'Hide' : 'Replace catalog'}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowField((s) => !s)}>
              {showField ? 'Hide' : 'Show'} field
            </Button>
          )}
        </div>
      </div>
      {!isLoaded && catalog !== null ? (
        <p className="text-xs text-sky-700 dark:text-sky-300 leading-relaxed">
          Load this once to identify Umphrey&apos;s McGee originals vs covers — covers (e.g. <em>YYZ</em>, <em>Burning
          Down the House</em>, <em>All Apologies</em>) will be filtered out of all analysis. Visit the API URL in your
          browser, copy the JSON (Ctrl/Cmd+A → copy), and paste below.
        </p>
      ) : isLoaded && !showField ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          {loadedSummary}. Click <span className="font-medium">Replace catalog</span> only if you want to overwrite
          the existing catalog (rarely needed — the catalog grows slowly as UM debuts new originals).
        </p>
      ) : (
        <p className="text-xs text-muted-foreground leading-relaxed">
          {loadedSummary ? `${loadedSummary}. ` : ''}Re-pasting will overwrite the existing catalog.
        </p>
      )}
      {showField || looksValid ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Paste the JSON from ${SONGS_API_URL} here…`}
          className="font-mono text-xs min-h-[8rem] max-h-72 w-full overflow-y-auto field-sizing-fixed resize-y"
        />
      ) : null}
    </div>
  )
}

function YearInputRow({
  year,
  status,
  value,
  onChange,
}: {
  year: string
  status: YearStatus
  value: string
  onChange: (v: string) => void
}) {
  // For loaded past years AND while status is unknown (checking), default to collapsed.
  // For required + current years, default to expanded.
  const [showField, setShowField] = useState(
    status.kind !== 'loaded' && status.kind !== 'checking'
  )

  const trimmed = value.trim()
  const looksValid = trimmed.length > 0 && (trimmed.startsWith('{') || trimmed.startsWith('['))
  let entriesHint = ''
  if (looksValid) {
    try {
      const parsed = JSON.parse(trimmed)
      let arr: unknown[] = []
      if (Array.isArray(parsed)) arr = parsed
      else if (parsed && typeof parsed === 'object') {
        const p = parsed as Record<string, unknown>
        if (Array.isArray(p.data)) arr = p.data
        else if (p.data && typeof p.data === 'object') {
          const d = p.data as Record<string, unknown>
          for (const k of Object.keys(d)) {
            if (Array.isArray(d[k])) {
              arr = d[k] as unknown[]
              break
            }
          }
        }
      }
      entriesHint = `${arr.length.toLocaleString()} entries parsed`
    } catch {
      entriesHint = 'invalid JSON'
    }
  }

  // Status badge for the year row
  const statusBadge = (() => {
    if (looksValid) {
      return (
        <Badge variant={entriesHint === 'invalid JSON' ? 'destructive' : 'secondary'}>
          {entriesHint}
        </Badge>
      )
    }
    if (status.kind === 'checking') {
      return (
        <Badge variant="secondary" className="gap-1">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Checking…
        </Badge>
      )
    }
    if (status.kind === 'required') {
      return (
        <Badge variant="destructive" className="gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          Required
        </Badge>
      )
    }
    if (status.kind === 'current') {
      if (status.loaded) {
        return (
          <Badge variant="default" className="gap-1 bg-sky-600 hover:bg-sky-600">
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            Current year — refresh for new shows
          </Badge>
        )
      }
      return (
        <Badge variant="default" className="gap-1 bg-sky-600 hover:bg-sky-600">
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          Current year — required
        </Badge>
      )
    }
    // loaded
    const l = status.loaded
    return (
      <Badge
        variant="secondary"
        className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300"
      >
        <Check className="h-3 w-3" />
        Loaded — {l.showCount} show{l.showCount === 1 ? '' : 's'}
        {l.lastRefreshedAt && (
          <span className="text-[10px] opacity-70 ml-1">
            · {new Date(l.lastRefreshedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </Badge>
    )
  })()

  // Helper text shown when an already-loaded year is collapsed
  const loadedHint =
    status.kind === 'loaded' && status.loaded ? (
      <span className="text-xs text-muted-foreground">
        {status.loaded.entryCount.toLocaleString()} setlist entries · last refreshed{' '}
        {status.loaded.lastRefreshedAt
          ? new Date(status.loaded.lastRefreshedAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })
          : 'unknown'}
      </span>
    ) : null

  return (
    <div
      className={`rounded-lg border p-4 space-y-3 ${
        status.kind === 'required' ? 'border-rose-300 bg-rose-50/40 dark:bg-rose-950/10' : ''
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <Label className="flex items-center gap-2 font-medium text-sm flex-wrap">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          {year} setlists
          {statusBadge}
        </Label>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(API_URL_FOR_YEAR(year), '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Open API URL
          </Button>
          {status.kind === 'checking' ? null : status.kind === 'loaded' && !looksValid ? (
            <Button variant="ghost" size="sm" onClick={() => setShowField((s) => !s)}>
              {showField ? 'Hide' : 'Refresh anyway'}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowField((s) => !s)}>
              {showField ? 'Hide' : 'Show'} field
            </Button>
          )}
        </div>
      </div>
      {status.kind === 'checking' ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          Checking whether this year is already loaded…
        </p>
      ) : status.kind === 'required' ? (
        <p className="text-xs text-rose-700 dark:text-rose-300 leading-relaxed">
          This year isn&apos;t loaded yet. Visit the API URL in your browser (Cloudflare will let you through),
          select all the JSON on the page (Ctrl/Cmd+A), copy, and paste into the field below.
        </p>
      ) : status.kind === 'current' ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          Visit the API URL in your browser, copy the JSON (Ctrl/Cmd+A → copy), and paste below to pick up any
          new shows the band has played in {year}.
        </p>
      ) : !showField ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          {loadedHint}. Click <span className="font-medium">Refresh anyway</span> if you want to overwrite this
          year&apos;s data (e.g. if a setlist was corrected on allthings.umphreys.com).
        </p>
      ) : (
        <p className="text-xs text-muted-foreground leading-relaxed">
          {loadedHint}. Re-pasting will overwrite the existing data for {year}.
        </p>
      )}
      {showField || looksValid ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Paste the JSON from ${API_URL_FOR_YEAR(year)} here…`}
          // field-sizing-fixed (overriding the default field-sizing-content) prevents
          // the textarea from growing horizontally when long lines are pasted — instead,
          // long content wraps within the fixed textarea width and grows vertically only.
          // overflow-y-auto + max-h-72 keep it from getting too tall.
          className="font-mono text-xs min-h-[8rem] max-h-72 w-full overflow-y-auto field-sizing-fixed resize-y"
        />
      ) : null}
    </div>
  )
}
